import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import {
  ProcessoEtapa,
  ProcessoEvento,
  ProcessoHistoricoAPIService,
  ProcessoHistoricoFiltros
} from 'src/app/shared/services/processo-historico-api.service';
import { UsuarioAPIService } from 'src/app/modules/usuario/services/usuario-api.service';

/**
 * Histórico do processo, em tabela.
 *
 * <p>Autocontido de propósito: recebe a origem por `@Input` e busca os próprios
 * dados. É o que permite embuti-lo tanto no pré-pedido quanto no pedido oficial
 * sem que a tela hospedeira precise saber nada sobre histórico — no caso do
 * pedido, sem tocar num componente de 1815 linhas.</p>
 *
 * <p>Informar `pedidoId` traz também o período em que o processo ainda era
 * pré-pedido: a conversão não encerra a rastreabilidade.</p>
 */
@Component({
  selector: 'app-processo-historico',
  templateUrl: './processo-historico.component.html',
  styleUrls: ['./processo-historico.component.scss']
})
export class ProcessoHistoricoComponent implements OnChanges {
  @Input() prePedidoId: number | null = null;
  @Input() pedidoId: number | null = null;
  /**
   * 'secao' = título de seção sobre linha fina (padrão dos cadastros);
   * 'cartao' = cartão com cabeçalho próprio, para telas no visual em cartões (.cadastro-moderno).
   */
  @Input() visual: 'secao' | 'cartao' = 'secao';

  public eventos: ProcessoEvento[] = [];
  public isCarregando = false;
  public isExportando = false;

  public pageNumber = 1;
  public totalItems = 0;

  /**
   * Recolhido mostra os últimos movimentos numa linha do tempo; expandido abre a
   * tabela com filtros, ordenação e paginação.
   *
   * <p>O padrão é recolhido porque o histórico fica <b>abaixo</b> do formulário:
   * aberto por inteiro, ele empurra as ações da tela para longe e domina a página
   * com um relatório que, na maior parte das vezes, ninguém abriu a tela para
   * ler. Os 5 últimos respondem "o que aconteceu por último" sem custo.</p>
   */
  public expandido = false;

  private static readonly ITENS_RESUMO = 5;
  private static readonly ITENS_PAGINA = 20;

  public get pageSize(): number {
    return this.expandido
      ? ProcessoHistoricoComponent.ITENS_PAGINA
      : ProcessoHistoricoComponent.ITENS_RESUMO;
  }

  public filtros: ProcessoHistoricoFiltros = { descendente: true, ordenarPor: 'dataEvento' };

  /**
   * Ações do filtro. Espelha `ProcessoEventoAcao` do backend — mesma duplicação de
   * vocabulário já registrada no `CLAUDE.md` da API.
   */
  public readonly acoes = [
    { valor: '',              rotulo: 'Todas' },
    { valor: 'criacao',       rotulo: 'Criação' },
    { valor: 'movimentacao',  rotulo: 'Enviado' },
    { valor: 'alteracao',     rotulo: 'Alteração' },
    { valor: 'inclusao',      rotulo: 'Inclusão' },
    { valor: 'exclusao',      rotulo: 'Exclusão' },
    { valor: 'devolucao',     rotulo: 'Devolvido' },
    { valor: 'aprovacao',     rotulo: 'Aprovação / Conversão' },
    { valor: 'justificativa_atraso', rotulo: 'Justificativa de atraso' }
  ];

  /**
   * Etapas do filtro: as do pré-pedido e as do pedido juntas.
   *
   * <p>Um histórico de pedido mostra desde o nascimento do pré-pedido, então oferecer
   * só um dos vocabulários esconderia metade dos eventos do filtro.</p>
   */
  public etapas: ProcessoEtapa[] = [];

  /**
   * Etapa escolhida no filtro, como objeto.
   *
   * <p>O select não pode carregar só o id: <b>os dois vocabulários colidem</b> — 1 é
   * "Em Elaboração" no pré-pedido e "Ag. Separação" no pedido. É preciso mandar id e
   * origem juntos, e é o objeto inteiro que carrega os dois.</p>
   */
  public etapaSelecionada: ProcessoEtapa | null = null;

  public get etapasPrePedido(): ProcessoEtapa[] {
    return this.etapas.filter(e => e.origem === 'pre_pedido');
  }

  public get etapasPedido(): ProcessoEtapa[] {
    return this.etapas.filter(e => e.origem === 'pedido');
  }

  /** Usuários, para o filtro por nome em vez de id. */
  public usuarios: { id: number; nome: string }[] = [];

  constructor(
    private api: ProcessoHistoricoAPIService,
    private usuarioApi: UsuarioAPIService,
    private toastr: ToastrService
  ) {
    this.carregarListasDeFiltro();
  }

  /**
   * Etapas e usuários dos selects.
   *
   * Falha aqui não derruba o histórico: o filtro fica sem opções, mas a tabela —
   * que é o que importa — continua carregando.
   */
  private async carregarListasDeFiltro(): Promise<void> {
    try {
      this.etapas = await firstValueFrom(this.api.listarEtapas()) ?? [];
    } catch { this.etapas = []; }

    // getUsers() — /usuarios/listar-usuarios —, e não getAll(): o getAll aplica o
    // recorte hierárquico e devolve só o próprio usuário e seus subordinados. Num
    // filtro de histórico isso esconderia justamente quem se quer auditar: quem
    // agiu no processo costuma ser de outro departamento. Mesma escolha da
    // distribuição de tarefas, pelo mesmo motivo.
    try {
      const res = await firstValueFrom(this.usuarioApi.getUsers()) as any;
      this.usuarios = (res?.items ?? res ?? []) as any[];
    } catch { this.usuarios = []; }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A origem chega depois do primeiro render, quando a tela hospedeira ainda
    // está carregando o registro. Sem reagir à mudança, o histórico buscaria com
    // id nulo e ficaria vazio para sempre.
    if (changes['prePedidoId'] || changes['pedidoId']) {
      if (this.temOrigem) this.carregar(1);
    }
  }

  get temOrigem(): boolean {
    return !!this.prePedidoId || !!this.pedidoId;
  }

  private get origem() {
    return { prePedidoId: this.prePedidoId, pedidoId: this.pedidoId };
  }

  /**
   * Sequência da última consulta disparada. Só a resposta que carrega o número
   * corrente pode escrever na tela.
   *
   * <p>Todo caminho passa por aqui — alternar resumo/tabela, filtrar, ordenar,
   * paginar —, e nenhum deles espera o anterior terminar. Alternar duas vezes
   * rápido deixa duas requisições no ar com tamanhos de página diferentes; se a
   * primeira responder por último, a tela fica no modo resumo exibindo os 20
   * registros da tabela, e só um novo clique conserta.</p>
   */
  private sequenciaConsulta = 0;

  async carregar(pagina = this.pageNumber): Promise<void> {
    if (!this.temOrigem) return;

    const sequencia = ++this.sequenciaConsulta;

    this.pageNumber = pagina;
    this.isCarregando = true;

    try {
      const res = await firstValueFrom(
        this.api.listar(this.origem, this.filtros, this.pageNumber, this.pageSize));

      if (sequencia !== this.sequenciaConsulta) return;

      this.eventos = res?.items ?? [];
      this.totalItems = res?.totalItems ?? 0;
    } catch {
      if (sequencia !== this.sequenciaConsulta) return;

      this.eventos = [];
      this.totalItems = 0;
      this.toastr.error('Não foi possível carregar o histórico.', 'Erro');
    } finally {
      // Só a consulta corrente desliga o indicador: uma resposta atrasada
      // apagaria o "carregando..." de uma busca ainda em curso.
      if (sequencia === this.sequenciaConsulta) this.isCarregando = false;
    }
  }

  /** Filtro alterado volta para a primeira página: a atual pode nem existir mais. */
  aplicarFiltros(): void {
    this.carregar(1);
  }

  limparFiltros(): void {
    this.filtros = { descendente: true, ordenarPor: 'dataEvento' };
    this.etapaSelecionada = null;
    this.carregar(1);
  }

  /** Passa id e origem da etapa escolhida para o filtro. */
  onEtapaChange(): void {
    this.filtros.statusId = this.etapaSelecionada?.id ?? null;
    this.filtros.etapaOrigem = this.etapaSelecionada?.origem ?? null;
    this.aplicarFiltros();
  }

  /**
   * De qual lado do processo o evento veio.
   *
   * <p>Sai dos próprios ids do registro, sem coluna nova: evento do pré-pedido tem
   * <c>prePedidoId</c>; do pedido, <c>pedidoId</c>. A aprovação carrega os dois — é a
   * fronteira — e conta como pedido, porque é o ato que o cria.</p>
   */
  origemDoEvento(e: ProcessoEvento): 'pedido' | 'pre_pedido' {
    return e.pedidoId ? 'pedido' : 'pre_pedido';
  }

  rotuloOrigem(e: ProcessoEvento): string {
    return this.origemDoEvento(e) === 'pedido' ? 'Pedido' : 'Pré-Pedido';
  }

  /** Clicar na coluna já ordenada inverte o sentido; em outra, ordena por ela. */
  ordenarPor(coluna: string): void {
    if (this.filtros.ordenarPor === coluna) {
      this.filtros.descendente = !this.filtros.descendente;
    } else {
      this.filtros.ordenarPor = coluna;
      this.filtros.descendente = true;
    }

    this.carregar(1);
  }

  iconeOrdenacao(coluna: string): string {
    if (this.filtros.ordenarPor !== coluna) return 'bx bx-sort-alt-2 text-muted';
    return this.filtros.descendente ? 'bx bx-sort-down' : 'bx bx-sort-up';
  }

  async exportar(): Promise<void> {
    if (!this.temOrigem) return;

    this.isExportando = true;
    try {
      const blob = await firstValueFrom(this.api.exportar(this.origem, this.filtros));

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `historico-${this.pedidoId ?? this.prePedidoId}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      this.toastr.error('Não foi possível exportar o histórico.', 'Erro');
    } finally {
      this.isExportando = false;
    }
  }

  /**
   * Etapas de devolução do pré-pedido. Um envio que parte de uma delas é um
   * reenvio — o processo já tinha voltado antes.
   */
  private static readonly ETAPAS_DEVOLUCAO = [3, 5];

  /** Aprovado e Cancelado: o processo termina, não segue adiante. */
  private static readonly ETAPAS_TERMINAIS = [6, 7];

  /**
   * Rótulo da ação, com uma distinção que não está gravada: <b>Reenviado</b>.
   *
   * <p>Derivado da etapa de origem em vez de virar uma sétima ação no banco. É a
   * mesma movimentação — quem filtra por "Enviado" quer ver os dois —, e o que
   * muda é só a leitura: ver "Reenviado" na linha diz, sem abrir nada, que aquele
   * processo bateu e voltou.</p>
   *
   * <p>Sair de uma etapa de devolução <b>não basta</b>: o cancelamento também é
   * gravado como movimentação, e cancelar é uma das duas saídas válidas de um
   * pré-pedido devolvido. Sem checar o destino, um processo encerrado apareceria
   * como "Reenviado" ao lado do próprio motivo do cancelamento.</p>
   */
  rotuloAcao(e: ProcessoEvento): string {
    const veioDeDevolucao =
      e.statusAnteriorId != null
      && ProcessoHistoricoComponent.ETAPAS_DEVOLUCAO.includes(e.statusAnteriorId);

    const seguiuAdiante =
      e.statusNovoId != null
      && !ProcessoHistoricoComponent.ETAPAS_TERMINAIS.includes(e.statusNovoId);

    if (e.acao === 'movimentacao' && veioDeDevolucao && seguiuAdiante)
      return 'Reenviado';

    return this.acoes.find(a => a.valor === e.acao)?.rotulo ?? e.acao;
  }

  /** Cor do badge por ação, para a coluna ser lida de relance. */
  classeAcao(acao: string): string {
    switch (acao) {
      case 'criacao':      return 'bg-secondary';
      case 'movimentacao': return 'bg-primary';
      case 'alteracao':    return 'bg-info text-dark';
      case 'inclusao':     return 'bg-success';
      case 'exclusao':     return 'bg-danger';
      case 'devolucao':    return 'bg-warning text-dark';
      case 'aprovacao':    return 'bg-success';
      case 'justificativa_atraso': return 'bg-warning text-dark';
      default:             return 'bg-light text-dark';
    }
  }

  /**
   * Token de cor da linha do tempo. Vira sufixo de classe CSS
   * (`evento-icone--success`), então é um nome curto e não uma classe do
   * Bootstrap: o círculo precisa de fundo claro e ícone escuro, combinação que
   * `bg-*` não dá.
   */
  corAcao(acao: string): string {
    switch (acao) {
      case 'criacao':      return 'neutro';
      case 'movimentacao': return 'primario';
      case 'alteracao':    return 'info';
      case 'inclusao':     return 'sucesso';
      case 'exclusao':     return 'perigo';
      case 'devolucao':    return 'alerta';
      case 'aprovacao':    return 'sucesso';
      case 'justificativa_atraso': return 'alerta';
      default:             return 'neutro';
    }
  }

  /** Ícone da bolinha da linha do tempo. */
  iconeAcao(acao: string): string {
    switch (acao) {
      case 'criacao':      return 'bx bx-plus';
      case 'movimentacao': return 'bx bx-right-arrow-alt';
      case 'alteracao':    return 'bx bx-edit-alt';
      case 'inclusao':     return 'bx bx-plus-circle';
      case 'exclusao':     return 'bx bx-trash';
      case 'devolucao':    return 'bx bx-undo';
      case 'aprovacao':    return 'bx bx-check-double';
      case 'justificativa_atraso': return 'bx bx-time-five';
      default:             return 'bx bx-dots-horizontal-rounded';
    }
  }

  /**
   * Alterna entre resumo e tabela cheia.
   *
   * <p>Recarrega porque o tamanho de página muda junto: sem isso, expandir
   * continuaria mostrando 5 linhas e paginando de 20 em 20.</p>
   */
  alternarExpansao(): void {
    this.expandido = !this.expandido;

    // Filtro aplicado no modo cheio não faz sentido num resumo que não os
    // exibe — recolher com filtro ativo mostraria "os últimos 5" de um
    // recorte invisível.
    if (!this.expandido) {
      this.filtros = { descendente: true, ordenarPor: 'dataEvento' };
      this.etapaSelecionada = null;
    }

    this.carregar(1);
  }

  /** Quantos registros ainda não estão à vista no resumo. */
  get restantes(): number {
    return Math.max(0, this.totalItems - this.eventos.length);
  }

  trackByEventoId(_i: number, e: ProcessoEvento): number { return e.id; }
}
